import { useEffect, useMemo, useState } from "react";

import { useGenerationSettings } from "~/entities/model";
import { getModelById } from "~/shared/data/chat-models";
import { useChatSettingsStore } from "~/shared/store/useChatSettingsStore";

import {
    useGetOrCreateDraftChat,
    type DraftChatInput,
    type DraftChatResult,
} from "./useGetOrCreateDraftChat";

export interface DraftChatScope {
    workspaceId?: string;
    projectId?: string;
    sceneId?: string;
}

export interface UseDraftChatForAttachmentsResult {
    /** Id заранее подготовленного draft-чата (null для text-режимов / до готовности). */
    draftChatId: string | null;
    /** Переиспускает/создаёт draft (тот же id) — для submit-пути. */
    run: (input: DraftChatInput) => Promise<DraftChatResult>;
    isPending: boolean;
}

/**
 * Готовит chatId для загрузки вложений в НОВОМ (ещё не сохранённом) чате.
 *
 * Скрепка без chatId неактивна (upload требует chatId для S3-scope), поэтому
 * reference-режимы (кадры/omni/motion), которым картинка/видео нужны ДО первого
 * сообщения, из пустого чата иначе не запустить. Draft создаём заранее — но
 * ТОЛЬКО когда текущий режим принимает вложения, чтобы text-режимы не плодили
 * пустые чаты. Draft переиспускается (`useGetOrCreateDraftChat`, один на
 * workspace/scope), сирот убирает hourly orphan_chat_cleanup.
 */
export function useDraftChatForAttachments({
    workspaceId,
    projectId,
    sceneId,
}: DraftChatScope): UseDraftChatForAttachmentsResult {
    const { run, isPending } = useGetOrCreateDraftChat();
    const modelId = useChatSettingsStore((s) => s.modelId);
    const mode = useChatSettingsStore((s) => s.mode);
    const settingsQuery = useGenerationSettings();

    const needsAttachment = useMemo(() => {
        const model = getModelById(settingsQuery.data, modelId);
        const currentMode = model?.modes.find((m) => m.name === mode) ?? null;
        return (currentMode?.maxInputImages ?? 0) > 0 || (currentMode?.maxInputVideos ?? 0) > 0;
    }, [settingsQuery.data, modelId, mode]);

    const [draftChatId, setDraftChatId] = useState<string | null>(null);
    // Сбрасываем при смене scope: draft другого проекта/сцены не «утянет» чат.
    useEffect(() => {
        setDraftChatId(null);
    }, [workspaceId, projectId, sceneId]);
    useEffect(() => {
        if (!workspaceId || draftChatId || isPending || !needsAttachment) return;
        let cancelled = false;
        void run({ workspaceId, projectId, sceneId }).then(({ chat }) => {
            if (!cancelled) setDraftChatId(chat.id);
        });
        return () => {
            cancelled = true;
        };
    }, [workspaceId, projectId, sceneId, draftChatId, isPending, needsAttachment, run]);

    return { draftChatId, run, isPending };
}
